import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from '../../api/ApiProvider.js';
import { useActiveClassroom } from '../../context/ActiveClassroomProvider.js';
import { getPageCatalogEntry, pageKeysByPlacement } from '@classy/shared';
import type { ClassroomDto } from '../../api/types.js';
import { CLASSROOM_PAGE_MODULES, type ClassroomPageKey } from '../../classroomPages/registry.js';
import { PageActionsContext } from '../../classroomPages/pageActionsContext.js';
import { GeneralTab } from './GeneralTab.js';
import { ClassroomHistory } from './ClassroomHistory.js';
import { RewardsSettings } from '../../rewards/RewardsSettings.js';
import { useRewardsAccess } from '../../rewards/access.js';
import { useClassroomPages } from '../../hooks/useClassroomPages.js';
import styles from './ClassroomDetail.module.css';

export interface ClassroomDetailProps {
  classroom: ClassroomDto;
  onBack: () => void;
}

/**
 * Pre-activation classroom configuration page. The user lands here after
 * picking a classroom from the hub. They can configure each enabled
 * page's settings via tabs, then "Start Teaching" to enter live
 * morning-meeting mode.
 *
 * The page-tab system is registry-driven: every classroom page module
 * (Home, Calendar, Day, …) declares its own settings UI in
 * src/classroomPages/<page>.module.tsx. ClassroomDetail just renders
 * the registry entries.
 */
export function ClassroomDetail({ classroom, onBack }: ClassroomDetailProps) {
  const api = useApi();
  const queryClient = useQueryClient();
  const { open } = useActiveClassroom();

  // Top-level tabs ('general' = Settings, 'history' = Day-in-review) plus one
  // per enabled page module. History is classroom-level and teacher-facing —
  // deliberately NOT one of the live morning-meeting pages.
  type DetailTab = 'general' | 'history' | 'rewards' | ClassroomPageKey;
  // Default to Home so the teacher lands on the morning-meeting greeting
  // + message panel. Archived classrooms open straight to Settings,
  // since the only useful action there (Unarchive) lives in that view.
  const [tab, setTab] = useState<DetailTab>(classroom.status === 'archived' ? 'general' : 'home');
  // The private Rewards tracker settings tab shows only for its owner's login.
  const rewardsAccess = useRewardsAccess();

  // Per-classroom enabled-pages list, now server-backed (follows the teacher
  // across devices). `useClassroomPages` exposes the same flat get/set the
  // General tab's PagesPicker, the settings tab strip, and the live
  // <MmPageNav/> / <LivePageNav/> all read — so they stay in sync, and a save
  // on the laptop shows up on the classroom board. Home is pinned (always
  // present, can't be removed); standalone pages route to their own group.
  const { enabledPages, setEnabledPages } = useClassroomPages(classroom.id);

  const addPage = (key: ClassroomPageKey) => {
    if (enabledPages.includes(key)) return;
    setEnabledPages([...enabledPages, key]);
  };

  const removePage = (key: ClassroomPageKey) => {
    setEnabledPages(enabledPages.filter((k) => k !== key));
    // If the user removed the page they're viewing, snap back to General.
    if (tab === key) setTab('general');
  };

  const movePage = (key: ClassroomPageKey, direction: 'left' | 'right') => {
    const idx = enabledPages.indexOf(key);
    if (idx < 0) return;
    const target = direction === 'left' ? idx - 1 : idx + 1;
    if (target < 0 || target >= enabledPages.length) return;
    const next = [...enabledPages];
    [next[idx], next[target]] = [next[target]!, next[idx]!];
    setEnabledPages(next);
  };

  // Reorder the full enabled-pages list — drag-and-drop in the tab strip
  // routes here. The strip, the Pages picker, and the live nav all read the
  // same server-backed list, so they stay in sync.
  const reorderPages = (nextOrder: ClassroomPageKey[]) => {
    setEnabledPages(nextOrder);
  };

  const toTab = (key: string) => {
    const m = CLASSROOM_PAGE_MODULES.find((mod) => mod.key === key);
    return m ? { key: m.key as DetailTab, label: m.label } : null;
  };

  // Two page lists, each rendered as a strip (same experience):
  //   - "Classroom Pages": Home (permanent, always on) + the standalone
  //     class-level pages (YouTube, Links). These are PERMANENT — always listed
  //     — with an enable/disable toggle. Disabled ones are greyed here and
  //     hidden in the live nav.
  //   - "Morning Meeting": the morning-meeting pages, kept on the existing
  //     add/remove + reorder flow (PageStore) below.
  const classroomPageTabs = useMemo(() => {
    const keys = ['home', ...pageKeysByPlacement('standalone')];
    return keys
      .map((key) => {
        const m = CLASSROOM_PAGE_MODULES.find((mod) => mod.key === key);
        if (!m) return null;
        return {
          key: m.key as DetailTab,
          label: m.label,
          enabled: key === 'home' || enabledPages.includes(key as ClassroomPageKey),
          toggleable: key !== 'home',
        };
      })
      .filter(
        (t): t is { key: DetailTab; label: string; enabled: boolean; toggleable: boolean } =>
          t !== null,
      );
  }, [enabledPages]);
  const mmTabs = useMemo(
    () =>
      enabledPages
        .filter((key) => getPageCatalogEntry(key)?.placement === 'morning-meeting')
        .map(toTab)
        .filter((t): t is { key: DetailTab; label: string } => t !== null),
    [enabledPages],
  );

  // Enable/disable a classroom page without changing which tab is open (so a
  // teacher can configure a page even while it's toggled off).
  const setPageEnabled = (key: ClassroomPageKey, enabled: boolean) => {
    if (enabled) {
      if (!enabledPages.includes(key)) setEnabledPages([...enabledPages, key]);
    } else {
      setEnabledPages(enabledPages.filter((k) => k !== key));
    }
  };

  // Drag-and-drop reorder state for the tab strip (mouse/trackpad). The
  // Pages picker's ‹/› buttons remain the touch/keyboard path.
  const [dragKey, setDragKey] = useState<ClassroomPageKey | null>(null);
  const dropOnTab = (targetKey: ClassroomPageKey) => {
    if (!dragKey || dragKey === targetKey) return setDragKey(null);
    const order = [...enabledPages];
    const from = order.indexOf(dragKey);
    const to = order.indexOf(targetKey);
    if (from < 0 || to < 0) return setDragKey(null);
    order.splice(from, 1);
    order.splice(to, 0, dragKey);
    reorderPages(order);
    setDragKey(null);
  };

  const archive = useMutation({
    mutationFn: () => api.classrooms.archive(classroom.id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['classrooms'] });
      onBack();
    },
  });
  const unarchive = useMutation({
    mutationFn: () => api.classrooms.unarchive(classroom.id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['classrooms'] });
    },
  });

  const isArchived = classroom.status === 'archived';

  const activeModule =
    tab === 'general' ? null : (CLASSROOM_PAGE_MODULES.find((m) => m.key === tab) ?? null);

  return (
    <div className={styles.detail} data-testid="classroom-detail">
      <section className={styles.heading}>
        <p className={styles.eyebrow}>Classroom</p>
        <EditableClassroomTitle classroom={classroom} />
        {classroom.subject || classroom.gradeLevel || classroom.schoolYear ? (
          <p className={styles.meta}>
            {[classroom.subject, classroom.gradeLevel, classroom.schoolYear]
              .filter(Boolean)
              .join(' · ')}
          </p>
        ) : null}
      </section>

      <header className={styles.topbar}>
        <div className={styles.leadActions}>
          <button
            type="button"
            className={`glass-pill ${styles.back}`}
            onClick={onBack}
            aria-label="Back to classrooms"
            data-testid="classroom-detail-back"
          >
            <BackArrow />
            <span>Classrooms</span>
          </button>
          <button
            type="button"
            className={
              tab === 'general'
                ? `glass-pill ${styles.settingsBtn} ${styles.settingsBtnActive}`
                : `glass-pill ${styles.settingsBtn}`
            }
            aria-pressed={tab === 'general'}
            onClick={() => setTab('general')}
            data-testid="classroom-tab-general"
          >
            <GearIcon />
            <span>Settings</span>
          </button>
          <button
            type="button"
            className={
              tab === 'history'
                ? `glass-pill ${styles.settingsBtn} ${styles.settingsBtnActive}`
                : `glass-pill ${styles.settingsBtn}`
            }
            aria-pressed={tab === 'history'}
            onClick={() => setTab('history')}
            data-testid="classroom-tab-history"
          >
            <HistoryIcon />
            <span>History</span>
          </button>
          {rewardsAccess ? (
            <button
              type="button"
              className={
                tab === 'rewards'
                  ? `glass-pill ${styles.settingsBtn} ${styles.settingsBtnActive}`
                  : `glass-pill ${styles.settingsBtn}`
              }
              aria-pressed={tab === 'rewards'}
              onClick={() => setTab('rewards')}
              data-testid="classroom-tab-rewards"
            >
              <span>Rewards</span>
            </button>
          ) : null}
        </div>

        <div className={styles.actions}>
          {!isArchived ? (
            <button
              type="button"
              className="glass-pill glass-pill--accent"
              onClick={() => open(classroom)}
              data-testid="classroom-activate"
            >
              Start Teaching
            </button>
          ) : null}
        </div>
      </header>

      <div className={styles.sectionGroup}>
        <h2 className={styles.sectionHeading}>Classroom Pages</h2>
        <nav className={`glass ${styles.tabs}`} role="tablist" aria-label="Classroom pages">
          {/* Enabled pages read normal (black); disabled ones are greyed. The
           * actual enable/disable lives inside each page's panel (click in). */}
          {classroomPageTabs.map(({ key, label, enabled }) => {
            const classes = [styles.tab];
            if (tab === key) classes.push(styles.tabActive);
            if (!enabled) classes.push(styles.tabDisabled);
            return (
              <button
                key={key}
                type="button"
                role="tab"
                aria-selected={tab === key}
                className={classes.join(' ')}
                onClick={() => setTab(key)}
                data-testid={`classroom-tab-${key}`}
              >
                {label}
              </button>
            );
          })}
        </nav>
      </div>

      {mmTabs.length > 0 ? (
        <div className={styles.sectionGroup}>
          <h2 className={styles.sectionHeading}>Morning Meeting</h2>
          <nav className={`glass ${styles.tabs}`} role="tablist" aria-label="Morning Meeting pages">
            {mmTabs.map(({ key, label }) => {
              const pageKey = key as ClassroomPageKey;
              const classes = [styles.tab];
              if (tab === key) classes.push(styles.tabActive);
              if (dragKey === pageKey) classes.push(styles.tabDragging);
              return (
                <button
                  key={key}
                  type="button"
                  role="tab"
                  aria-selected={tab === key}
                  className={classes.join(' ')}
                  onClick={() => setTab(key)}
                  draggable
                  onDragStart={() => setDragKey(pageKey)}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={() => dropOnTab(pageKey)}
                  onDragEnd={() => setDragKey(null)}
                  title="Drag to reorder"
                  data-testid={`classroom-tab-${key}`}
                >
                  {label}
                </button>
              );
            })}
          </nav>
        </div>
      ) : null}

      <section className={styles.body}>
        {tab === 'general' ? (
          <div className={`glass glass--solid ${styles.panel}`}>
            <GeneralTab
              classroomId={classroom.id}
              enabledPages={enabledPages}
              onAddPage={addPage}
              onRemovePage={removePage}
              onMovePage={movePage}
            />
            {/* Archive lives here (not as a prominent top-level action) —
             * it's a rare, classroom-level operation. */}
            <div className={styles.dangerZone}>
              {!isArchived ? (
                <button
                  type="button"
                  className={`glass-pill ${styles.archive}`}
                  onClick={() => archive.mutate()}
                  disabled={archive.isPending}
                  data-testid="classroom-archive"
                >
                  {archive.isPending ? 'Archiving…' : 'Archive classroom'}
                </button>
              ) : (
                <button
                  type="button"
                  className="glass-pill"
                  onClick={() => unarchive.mutate()}
                  disabled={unarchive.isPending}
                  data-testid="classroom-unarchive"
                >
                  {unarchive.isPending ? 'Unarchiving…' : 'Unarchive classroom'}
                </button>
              )}
            </div>
          </div>
        ) : null}
        {tab === 'history' ? <ClassroomHistory classroom={classroom} /> : null}
        {tab === 'rewards' && rewardsAccess ? (
          <div className={`glass glass--solid ${styles.panel}`}>
            <RewardsSettings />
          </div>
        ) : null}
        {activeModule
          ? (() => {
              const placement = getPageCatalogEntry(tab)?.placement;
              if (placement === 'morning-meeting') {
                // Morning Meeting pages use add/remove: the panel header shows
                // "Remove page" (mirrors the picker's ✕).
                return (
                  <PageActionsContext.Provider
                    value={{
                      pageLabel: activeModule.label,
                      onRemove: () => removePage(tab as ClassroomPageKey),
                    }}
                  >
                    <activeModule.SettingsPanel classroom={classroom} />
                  </PageActionsContext.Provider>
                );
              }
              if (placement === 'standalone') {
                // Standalone classroom pages (YouTube, Links): the panel header
                // shows the enable/disable toggle (no add/remove).
                const key = tab as ClassroomPageKey;
                return (
                  <PageActionsContext.Provider
                    value={{
                      pageLabel: activeModule.label,
                      toggle: {
                        enabled: enabledPages.includes(key),
                        onToggle: (next) => setPageEnabled(key, next),
                      },
                    }}
                  >
                    <activeModule.SettingsPanel classroom={classroom} />
                  </PageActionsContext.Provider>
                );
              }
              // Home (pinned, always on): no remove, no toggle.
              return <activeModule.SettingsPanel classroom={classroom} />;
            })()
          : null}
      </section>
    </div>
  );
}

function BackArrow() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="16"
      height="16"
      aria-hidden="true"
      focusable="false"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M15 18l-6-6 6-6" />
    </svg>
  );
}

function HistoryIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="16"
      height="16"
      aria-hidden="true"
      focusable="false"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M3 3v5h5" />
      <path d="M3.05 13A9 9 0 1 0 6 5.3L3 8" />
      <path d="M12 7v5l3 2" />
    </svg>
  );
}

function GearIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="16"
      height="16"
      aria-hidden="true"
      focusable="false"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  );
}

/**
 * Inline-editable classroom title. The h1 doubles as the click
 * affordance — tap it, the heading flips to a text input that picks
 * up the same styling. Enter or blur commits via PATCH /classrooms/:id;
 * Esc cancels and reverts to the previous name. The hub list refetches
 * on save so the renamed classroom shows up everywhere right away.
 */
function EditableClassroomTitle({ classroom }: { classroom: ClassroomDto }) {
  const api = useApi();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(classroom.name);

  // Re-sync draft when the upstream classroom changes (e.g. another
  // tab renamed it, or our save just succeeded).
  useEffect(() => {
    setDraft(classroom.name);
  }, [classroom.name]);

  const save = useMutation({
    mutationFn: (next: string) => api.classrooms.patch(classroom.id, { name: next }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['classrooms'] });
      setEditing(false);
    },
    onError: () => {
      // Roll back to the server value and surface the error via the
      // browser's default render — the inline form has no banner space.
      setDraft(classroom.name);
      setEditing(false);
    },
  });

  const commit = () => {
    const trimmed = draft.trim();
    if (!trimmed || trimmed === classroom.name) {
      setDraft(classroom.name);
      setEditing(false);
      return;
    }
    save.mutate(trimmed);
  };

  if (!editing) {
    return (
      <h1
        className={styles.title}
        onClick={() => setEditing(true)}
        title="Click to rename"
        data-testid="classroom-title"
      >
        {classroom.name}
        <span className={styles.editGlyph} aria-hidden="true">
          ✎
        </span>
      </h1>
    );
  }

  return (
    <input
      type="text"
      className={styles.titleInput}
      value={draft}
      maxLength={120}
      autoFocus
      disabled={save.isPending}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          commit();
        } else if (e.key === 'Escape') {
          e.preventDefault();
          setDraft(classroom.name);
          setEditing(false);
        }
      }}
      aria-label="Classroom name"
      data-testid="classroom-title-input"
    />
  );
}
